/**
 *
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries.
 *
 */
import PerfectScrollbar from 'perfect-scrollbar';
import SweetAlert from 'sweetalert2';
import Ladda from 'ladda';
import Toastify from 'toastify';
import GoogleMapsLoader from 'google-maps';
import Vue from 'vue/dist/vue';
import VueRouter from 'vue-router';
import Vuex from 'vuex';
// import Echo from "laravel-echo";
import Axios from 'axios/dist/axios';
// import JWTManager from "jwt-manager";
import VueFormComponents from 'vue-form-components';
import MultiSelect from 'vue-multiselect';
import Dropzone from 'vue2-dropzone';

/* App sass */
// import 'assets/style/sass/axis.scss';
import '../node_modules/@ais/statement-renderer/dist/statement-renderer.css';

/**
 *
 * Now we are going to pull in all of our application
 * specific libraries and Vue components
 *
 */
import Modal from './components/Generic/Modal.vue';
import FormNavigationTabs from './components/Generic/Form/NavigationTabs.vue';
import AddressLookup from './components/Generic/AddressLookup.vue';
import Datepicker from './components/Generic/Form/Datepicker.vue';
import DatepickerInput from './components/Generic/Form/DatepickerInput';
import DateTimePicker from './components/Generic/Form/DateTimepicker.vue';
import DateOfBirth from './components/Generic/Form/DateOfBirth.vue';
import CurrencyInput from './components/Generic/Form/CurrencyInput.vue';
import FileInput from './components/Generic/Form/FileUpload.vue';
import SelectList from './components/Generic/Form/SelectList.vue';
import Spinner from './components/Generic/Form/Spinner.vue';
import FleetVehicleLookup from './components/Generic/Form/FleetVehicleLookup.vue';
import Pagination from './components/Generic/Pagination.vue';
import PreLoader from './components/Generic/PreLoader.vue';
import ClaimHandler from './components/Generic/ClaimHandler.vue';
import DeployPersonalInjury from './components/Generic/DeployPersonalInjury.vue';
import Routes from './routes';
import Form from './Classes/Form';
import Ajax from './Classes/Ajax';
import FormButton from './components/Generic/Form/FormButton.vue';
import GlobalMixin from './mixins/global';
import DataStore from './store';
import DateManager from './Classes/DateManager';
import Auth from './auth';
import Token from './token';
import IsObjectEmpty from './Classes/IsObjectEmpty';
import PreloaderHandler from './Classes/PreloaderHandler';
import App from './App';
import Config from './config';

/**
 *
 * Now we are going to bind certain dependencies to the window,
 * allowing us to access them throughout our application.
 *
 */
window.Ps = PerfectScrollbar;
window.swal = SweetAlert;
window.Ladda = Ladda;
window.toastify = Toastify;
window.GoogleMapsLoader = GoogleMapsLoader;
window.Vue = Vue;
// window.JWTManager = new JWTManager();
window.axios = Axios;
window.Token = new Token();
window.Ajax = new Ajax();
window.Auth = new Auth();
window.Form = Form;
window.DateManager = new DateManager();
window.IsObjectEmpty = new IsObjectEmpty();
window.preloaderHandler = new PreloaderHandler();

/**
 *
 * Configure libraries which have been bound to the window,
 * that way we do not need to keep making the same settings while
 * using the library
 *
 */

window.AxisUK = {
  api: Config.apiBaseUrl,
  storage: Config.storageUrl,
  echo: {
    port: Config.echoPort
  },
  ais: {
    api: Config.aisApiUrl
  },
  docusign: {
    clientIntegrationId: Config.docusignClientIntegrationId
  }
};

window.GoogleMapsLoader.KEY = Config.googleMapsKey;
window.GoogleMapsLoader.VERSION = 'weekly';
window.swal.setDefaults({
  confirmButtonColor: '#00AFB7',
  cancelButtonColor: '#bf5329'
});
window.axios.defaults.headers.common = {
  'Access-Control-Allow-Origin': '*',
  'X-Requested-With': 'XMLHttpRequest'
};
window.axios.defaults.baseURL = window.AxisUK.api;
window.axios.defaults.withCredentials = true;
/**
 *
 * We are going to register some global Vue components.
 * Here it will make each component available to every
 * route, without us having to manually import it each
 * time. This is useful for components which will be
 * used often such as form components.
 *
 */
window.Vue.component('inputbox', VueFormComponents.InputBoxComponent);
window.Vue.component('input-block', VueFormComponents.TextAreaComponent);
window.Vue.component('selectlist', SelectList);
window.Vue.component('toggleswitch', VueFormComponents.SwitchComponent);
window.Vue.component('form-button', FormButton);
window.Vue.component('form-navigation-tabs', FormNavigationTabs);
window.Vue.component('modal', Modal);
window.Vue.component('pagination', Pagination);
window.Vue.component('address-lookup', AddressLookup);
window.Vue.component('datepicker', Datepicker);
window.Vue.component('datetimepicker', DateTimePicker);
window.Vue.component('datepicker-input', DatepickerInput);
window.Vue.component('date-of-birth', DateOfBirth);
window.Vue.component('currencyinput', CurrencyInput);
window.Vue.component('multiselect', MultiSelect);
window.Vue.component('file-input', FileInput);
window.Vue.component('spinner', Spinner);
window.Vue.component('dropzone', Dropzone);
window.Vue.component('preloader', PreLoader);
window.Vue.component('claim-handler', ClaimHandler);
window.Vue.component('deploy-personal-injury', DeployPersonalInjury);
window.Vue.component('fleet-vehicle-lookup', FleetVehicleLookup);

/**
 *
 * Now we are going to tell Vue we are going to use Vuex
 * and create a new Vuex store using the object we imported
 *
 */
window.Vue.use(Vuex);
const store = new Vuex.Store(DataStore);

/**
 *
 * Now we are going to register our global mixin which we imported
 * earlier on. This gives us access to a variety of useful helper
 * methods to use in every component
 *
 */
window.Vue.mixin(GlobalMixin);

/**
 *
 * Here we are going to register a listener for the Vuex store.
 * Every time a mutation takes place, we will listen for the set
 * user mutation. Then we will get the notifications for that
 * user.
 *
 */
function getNotifications() {
  window.Ajax.get('/notifications')
    .then(response => {
      store.commit('Notifications/set', response.data);
    })
    .catch(error => {});
}

/**
 *
 * Now we are going to set up a set of unauthenticated routes.
 * This is for checking if the user is correctly authenticated
 * to access the route
 *
 */
const unauthenticatedRoutes = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/reset-expired-password'
];

/**
 *
 * Here we are going to assign the Router we pulled in
 * to a variable and tell Vue that we are going to be
 * using the VueRouter plugin for our routing.
 *
 */
window.Vue.use(VueRouter);
const router = Routes;
window.Router = router;

window.Vue.filter('date', function(date, format = 'DD/MM/YYYY') {
  return window.DateManager.format(date, format);
});

store.subscribe((mutation, state) => {
  if (mutation.type === 'setUser') {
    if (!state.listener) {
      // window.Echo.private(`User.${state.user.identifier}`).listen('.Illuminate.Notifications.Events.BroadcastNotificationCreated', (e) => {
      //     window.toastr.success(e.body, e.title);
      //
      //     getNotifications();
      // });

      store.commit('enableListener');
    }

    // When the user logs out we reset the user to an empty object. Here,
    // we check if the user's id is set, and if not we remove the
    // authenticated user from the auth service.
    if (!mutation.payload.id) {
      window.Auth.removeUser();
    }

    getNotifications();
  }
});

/**
 *
 * Here, we register a before each hook to the router. This allows us
 * to check if the authenticated user has permission to access the
 * requested router. If they do not then we redirect them to the
 * dashboard.
 *
 * We will also set up authentication rules so that users cannot visit
 * pages without authenticating first.
 *
 */
router.beforeEach((to, from, next) => {
  if (unauthenticatedRoutes.indexOf(to.path) === -1) {
    window.Auth.getUser()
      .then(user => {
        // If the route has a permission set against it and the user does not have the relevant permission, then
        // redirect the user to the dashboard
        if (
          to.meta?.permission &&
          user.permissions.indexOf(to.meta?.permission) < 0
        ) {
          next('/dashboard');
        } else {
          next();
        }
      })
      .catch(error => {
        next('/login');
      });
  } else {
    next();
  }
});

/**
 *
 * Now we are going to create the main Vue instance for our application
 * as well as registering the router and the Vuex store.
 *
 */
const vm = new window.Vue({
  router,
  store,
  render: h => h(App),
  created() {
    if (unauthenticatedRoutes.indexOf(this.$route.path) > -1) {
      //
    } else {
      this.checkAuth();
    }
  },

  data() {
    return {
      render: false
    };
  },

  methods: {
    checkAuth() {
      window.Auth.getUser()
        .then(data => {
          if (!data.identifier) {
            return this.redirectToLogin();
          }

          this.render = true;

          this.$store.commit('setUser', data);

          if (this.$router.path === '/login') {
            this.$router.push('/dashboard');
          }

          this.getRecords();
        })
        .catch(error => {
          this.reAuthorize();
        });
    },

    redirectToLogin() {
      this.render = true;
      this.$router.push('/login');
    },

    getRecords() {
      this.$store.commit('Titles/retrieve');
      this.$store.commit('Roles/retrieve');
      this.$store.commit('SecurityQuestions/retrieve');
      this.$store.commit('ContactMethods/retrieve');
      this.$store.commit('ContactTimes/retrieve');
      this.$store.commit('VehicleGroups/retrieve');
      this.$store.commit('Genders/retrieve');
      this.$store.commit('VehicleMakes/retrieve');
      this.$store.commit('VehicleTransmissions/retrieve');
      this.$store.commit('VehicleTypes/retrieve');
      this.$store.commit('LicenceAuthorities/retrieve');
      this.$store.commit('Insurers/retrieve');
      this.$store.commit('CoverLevels/retrieve');
      this.$store.commit('VehicleUseTypes/retrieve');
      this.$store.commit('IncidentClaimTypes/retrieve');
      this.$store.commit('IncidentStatus/retrieve');
    },

    reAuthorize() {
      window.Auth.refreshToken()
        .then(data => {})
        .catch(error => {
          if (unauthenticatedRoutes.indexOf(this.$route.path) === -1) {
            this.$router.push('/login');
          }
        });
    }
  }
}).$mount('#app');
